import { store } from "./src/redux/store";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import Route from "./src/navigation/main";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { ActivityIndicator, LogBox, Platform, StyleSheet, Text, View } from "react-native";
import AudioModeController from "./src/components/general/audioModeController";
import ScreenCover from "./src/components/general/screenCover";
import DesktopHome from "./src/screens/desktopHome";
import { registerMobileHlsCache, watchPlaybackCachePolicy } from "./src/services/mobileHlsCache";
import { checkVisitorAccess } from "./src/services/visitorAccess";
import { detectWebViewMode } from "./src/utils/webViewMode";
import "./src/styles/fonts";
import { ShopProvider } from "./src/commerce/ShopProvider";
import { recordShareLanding } from "./src/services/shareTracking";
import { localizedSiteTitle } from "./src/i18n/strings";

LogBox.ignoreLogs([
  "Require cycle:",
  "You are initializing Firebase Auth for React Native without providing",
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      staleTime: Infinity,
    },
  },
});

export default function App() {
  useEffect(() => { recordShareLanding(); }, []);
  const [visitorAccess, setVisitorAccess] = useState<"checking" | "allowed" | "blocked">(
    Platform.OS === "web" ? "checking" : "allowed",
  );
  const [blockedMessage, setBlockedMessage] = useState("");
  const desktopWeb =
    Platform.OS === "web" &&
    detectWebViewMode() === "desktop";

  useEffect(() => {
    if (Platform.OS === "web") {
      document.title = localizedSiteTitle();
      registerMobileHlsCache().catch(() => {});
      return watchPlaybackCachePolicy();
    }
  }, [desktopWeb]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    let active = true;
    let completed = false;
    const failOpenTimer = window.setTimeout(() => {
      if (!active || completed) return;
      completed = true;
      setBlockedMessage("");
      setVisitorAccess("allowed");
    }, 6_000);

    checkVisitorAccess("site").then((decision) => {
      if (!active || completed) return;
      completed = true;
      window.clearTimeout(failOpenTimer);
      setBlockedMessage(decision.message || "抱歉，当前访问不符合本站访问策略。");
      setVisitorAccess(decision.allowed ? "allowed" : "blocked");
    }).catch(() => {
      if (!active || completed) return;
      completed = true;
      window.clearTimeout(failOpenTimer);
      setVisitorAccess("allowed");
    });
    return () => {
      active = false;
      window.clearTimeout(failOpenTimer);
    };
  }, []);

  if (Platform.OS === "web" && visitorAccess !== "allowed") {
    return (
      <View style={styles.accessGate}>
        {visitorAccess === "checking" ? (
          <><ActivityIndicator color="#d7a955" /><Text style={styles.accessText}>正在检查访问权限…</Text></>
        ) : (
          <><Text style={styles.accessTitle}>访问受限</Text><Text style={styles.accessText}>{blockedMessage}</Text></>
        )}
      </View>
    );
  }

  const app = (
    <GestureHandlerRootView style={desktopWeb ? styles.desktopFrame : styles.appFrame}>
      <Provider store={store}>
        <ShopProvider>
          {!desktopWeb && <AudioModeController />}
          <QueryClientProvider client={queryClient}>
            {desktopWeb ? <DesktopHome /> : <Route />}
          </QueryClientProvider>
          {!desktopWeb && <ScreenCover />}
        </ShopProvider>
      </Provider>
    </GestureHandlerRootView>
  );

  if (Platform.OS === "web") {
    return <View style={styles.webShell}>{app}</View>;
  }

  return app;
}

const styles = StyleSheet.create({
  accessGate: {
    flex: 1,
    minHeight: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: 28,
    backgroundColor: "#090909",
  },
  accessTitle: { color: "#d7a955", fontSize: 26, fontWeight: "700", marginBottom: 12 },
  accessText: { color: "#d0d0d0", fontSize: 16, textAlign: "center", marginTop: 12 },
  webShell: {
    flex: 1,
    width: "100%",
    minHeight: "100%",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#050505",
  },
  appFrame: {
    flex: 1,
    width: "100%",
    maxWidth: Platform.OS === "web" ? 430 : undefined,
    backgroundColor: "black",
    overflow: "hidden",
  },
  desktopFrame: {
    flex: 1,
    width: "100%",
    minHeight: "100%",
    backgroundColor: "#090909",
  },
});
